<template>
    <analytics-sidebar-wrapper @reset="emit('reset')">
        <analytics-blast-forecast-sender-sidebar
            v-if="FEATURES.contact_groups"
            :orgUnitId="props.orgUnitId"
            v-model:senderTypes="fetchParams.sender_types"
            v-model:orgUnitIdModel="fetchParams.org_unit_id"
            v-model:groupId="fetchParams.group_id"
        />

        <div class="col-span-4 lg:col-span-4">
            <analytics-sidebar-checkboxes v-model="fetchParams.statuses" :options="statusesOptions">
                <template v-slot:title>Status</template>
            </analytics-sidebar-checkboxes>
        </div>

        <div class="col-span-4 lg:col-span-4">
            <analytics-sidebar-checkboxes v-model="fetchParams.audience_types" :options="audienceTypesOptionsList">
                <template v-slot:title> Audience Type </template>
            </analytics-sidebar-checkboxes>
        </div>

        <div class="col-span-4 lg:col-span-4">
            <analytics-sidebar-category v-model="fetchParams.category" />
        </div>

        <div class="col-span-4 lg:col-span-4">
            <analytics-sidebar-box @reset="resetContentFilters">
                <template v-slot:title> Content Filters </template>

                <div class="flex flex-col gap-3 text-sm">
                    <div data-test="emoji">
                        <div class="text-xs font-semibold mb-1.5">Emoji</div>
                        <content-filter-toggle v-model="fetchParams.has_emoji" />
                    </div>

                    <div data-test="urls">
                        <div class="text-xs font-semibold mb-1.5">URL</div>
                        <content-filter-toggle v-model="fetchParams.has_urls" />
                    </div>

                    <div data-test="variables">
                        <div class="text-xs font-semibold mb-1.5">Variables</div>
                        <content-filter-toggle v-model="fetchParams.has_variables" />
                    </div>

                    <div data-test="media">
                        <div class="text-xs font-semibold mb-1.5">Media</div>
                        <content-filter-toggle v-model="fetchParams.has_media" />
                    </div>
                </div>
            </analytics-sidebar-box>
        </div>

        <analytics-blast-forecast-locked-snippet-sidebar
            :orgUnitId="props.orgUnitId"
            v-model="fetchParams.snippet_id"
        />
    </analytics-sidebar-wrapper>
</template>

<script lang="ts" setup>
import AnalyticsSidebarWrapper from '../partial/sidebar/AnalyticsSidebarWrapper.vue';
import { inject, type PropType } from 'vue';
import AnalyticsSidebarCheckboxes from '../partial/sidebar/AnalyticsSidebarCheckboxes.vue';
import AnalyticsSidebarBox from '../partial/sidebar/AnalyticsSidebarBox.vue';
import AnalyticsSidebarCategory from '../partial/sidebar/AnalyticsSidebarCategory.vue';
import ContentFilterToggle from './components/ContentFilterToggle.vue';
import { audienceTypes, CAMPAIGN_STATUSES, statuses } from '~/constants/campaign';
import AnalyticsBlastForecastSenderSidebar from './AnalyticsBlastForecastSenderSidebar.vue';
import AnalyticsBlastForecastLockedSnippetSidebar from './AnalyticsBlastForecastLockedSnippetSidebar.vue';
import type { GetBlastForecastRequest } from '~/services/blastForecast/blastForecastClient.types';
import type { Features } from '~/provides/features.types';

const FEATURES = inject('FEATURES') as Features;

const props = defineProps({
    orgUnitId: {
        type: Number,
    },
});

const audienceTypesOptionsList = audienceTypes().map((audienceType) => ({
    value: audienceType.name,
    label: audienceType.label,
}));

const statusesOptions = [
    { value: statuses.DRAFT, label: CAMPAIGN_STATUSES.draft.label },
    { value: statuses.SCHEDULED, label: CAMPAIGN_STATUSES.scheduled.label },
    { value: statuses.PUBLISHED, label: CAMPAIGN_STATUSES.published.label },
    { value: statuses.COMPLETED, label: CAMPAIGN_STATUSES.completed.label },
];

const fetchParams = defineModel({
    type: Object as PropType<GetBlastForecastRequest>,
    required: true,
});

const emit = defineEmits(['reset']);

const resetContentFilters = () => {
    fetchParams.value.has_emoji = undefined;
    fetchParams.value.has_urls = undefined;
    fetchParams.value.has_variables = undefined;
    fetchParams.value.has_media = undefined;
};
</script>
